<template>
  <PageHeader :showBreadcrumb="true" />
  <form v-if="(authStore.isAdminUser || authStore.isNetworkAdequacyUser) && appStore.networks.length > 0" @submit.stop.prevent="onSubmit">
    <div class="container">
      <DataTable v-model:editingRows="editingRows" v-model:filters="filters" :value="appStore.getNetworksAll()"
                 editMode="row" dataKey="id" @row-edit-save="onRowEditSave" @row-edit-init="onRowEditInit" @row-edit-cancel="onRowEditCancel"
                 stripedRows showGridlines paginator :rows="25" filterDisplay="row"
                 :pt="{
                   table: { style: 'min-width: 50rem' },
                   column: {
                     bodycell: ({ state }) => ({
                       style:  state['d_editing']&&'padding-top: 0.75rem; padding-bottom: 0.75rem'
                     })
                   }
                 }">
        <template #header>
          <div class="header-components gap-3">
            <Button icon="pi pi-filter-slash" label="Clear Filters" rounded @click="clearFilter()" />
            <IconField>
              <InputText v-model="filters['global'].value" label="Keyword Search" aria-label="Keyword Search" placeholder="Keyword Search" />
              <InputIcon>
                <i class="pi pi-search" ></i>
              </InputIcon>
            </IconField>
          </div>
        </template>
        <template #empty> No Data. </template>
        <Column field="carrier" header="Entity" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText id="carrier-filter" v-model="filterModel.value" type="text" aria-label="Carrier Filter" @input="filterCallback()" placeholder="Any" style="width: 100%" />
          </template>
        </Column>
        <Column field="networkName" header="Network Name" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText id="network-name-filter" v-model="filterModel.value" type="text" aria-label="Network Name Filter" @input="filterCallback()" placeholder="Any" style="width: 100%" />
          </template>
          <template #editor>
            <ValidatedInput
              id="networkName"
              label="Network Name"
              v-model="rowForm.formData.networkName"
              :validation="rowForm.v$?.value?.networkName"
              :inputComponent="InputText"
              :inputAttrs="{
                placeholder: 'Network Name',
                fluid: true
              }"
            />
          </template>
        </Column>
        <Column field="networkId" header="Network ID" :sortable="true">
          <template #filter="{ filterModel, filterCallback }">
            <InputText id="network-id-filter" v-model="filterModel.value" type="text" aria-label="Network ID Filter" @input="filterCallback()" placeholder="Any" style="width: 100%" />
          </template>
          <template #editor>
            <ValidatedInput
              id="networkId"
              label="Network ID"
              v-model="rowForm.formData.networkId"
              :validation="rowForm.v$?.value?.networkId"
              :inputComponent="InputText"
              :inputAttrs="{
                placeholder: 'Network ID',
                fluid: true
              }"
            />
          </template>
        </Column>
        <Column field="marketType" header="Market Type" :sortable="true" >
          <template #filter="{ filterModel, filterCallback }">
            <MultiSelect v-model="filterModel.value" @change="filterCallback()" :options="marketTypes" optionLabel="label" optionValue="value" placeholder="Any" :maxSelectedLabels="1">
              <template #option="slotProps">
                <Tag :value="slotProps.option.value" :severity="getSeverity(slotProps.option.value)"/>
              </template>
            </MultiSelect>
          </template>
          <template #editor>
            <ValidatedInput
              id="marketType"
              label="Market Type"
              v-model="rowForm.formData.marketType"
              :validation="rowForm.v$?.value?.marketType"
              :inputComponent="Select"
              :inputAttrs="{
                options: marketTypes,
                optionLabel: 'label',
                optionValue: 'value',
                placeholder: 'Select Market Type',
                fluid: true,
                optionTemplate: marketTypeOptionTemplate
              }"
            />
          </template>
          <template #body="slotProps">
            <Tag :value="slotProps.data.marketType" :severity="getSeverity(slotProps.data.marketType)"/>
          </template>
        </Column>
        <Column field="status" header="Network Status" :sortable="true">
          <template #editor>
            <ValidatedInput
              id="status"
              label="Network Status"
              v-model="rowForm.formData.status"
              :validation="rowForm.v$?.value?.status"
              :inputComponent="Select"
              :inputAttrs="{
                options: statuses,
                optionLabel: 'label',
                optionValue: 'value',
                placeholder: 'Select Status',
                fluid: true,
                optionTemplate: statusOptionTemplate
              }"
            />
          </template>
          <template #body="slotProps">
            <Tag :value="slotProps.data.status" :severity="getSeverity(slotProps.data.status)" />
          </template>
        </Column>
        <Column header="Actions" :rowEditor="true" style="width: 10%; min-width: 8rem" bodyStyle="text-align: center"/>
      </DataTable>
    </div>
  </form>
</template>

<script setup>
import {h, ref} from 'vue';
import PageHeader from "@/components/common/PageHeader.vue";
import {useAppStore} from "@/stores/appStore.js";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Button from "primevue/button";
import MultiSelect from "primevue/multiselect";
import {FilterMatchMode} from "@primevue/core/api";
import {NETWORK_MARKET_TYPE, NETWORK_STATUSES} from "@/utils/constants.js";
import {updateNetwork} from '@/services/networkService.js';
import {NetworkModel} from "@/models/NetworkModel.js";
import {useAuthStore} from "@/stores/authStore.js";
import ValidatedInput from "@/components/common/ValidatedInput.vue";
import {updateNetworkValidationRules} from "@/validators/networks/updateNetworkValidationRules.js";
import {useRowForm} from "@/composables/useRowForm.js";
import {useRowEditor} from "@/composables/useRowEditor.js";

const appStore = useAppStore();
const authStore = useAuthStore();

const editingRows = ref([]);

const initFilters = () => {
  return {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    carrier: { value: null, matchMode: FilterMatchMode.CONTAINS },
    networkName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    networkId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    marketType: { value: null, matchMode: FilterMatchMode.IN },
  };
};

const filters = ref(initFilters());

const clearFilter = () => {
  filters.value = initFilters();
};

const rowForm = useRowForm(() => updateNetworkValidationRules());

// use composable helpers for editor behavior
const { stopPrimeVueEvent, getCurrentRowId, reopenEditorForId, showToast } = useRowEditor(rowForm, editingRows);

const statuses = ref(
    Object.entries(NETWORK_STATUSES).map(([key, value]) => ({
      label: key,
      value: value,
    }))
);

const getSeverity = (value) => {
  // Handle status values
  switch (value) {
    case NETWORK_STATUSES.Reviewing:
      return 'warn';

    case NETWORK_STATUSES.Inactive:
      return 'danger';

    case NETWORK_STATUSES.Active:
      return 'success';

    // Handle market type values
    case 'PBM':
      return 'info';

    default:
      return null;
  }
};

const marketTypes = ref(
    Object.entries(NETWORK_MARKET_TYPE).map(([key, value]) => ({
      label: key,
      value: value,
    }))
);

// Render template for market type select options
const marketTypeOptionTemplate = (slotProps) => {
  return h(Tag, { value: slotProps.option.value, severity: getSeverity(slotProps.option.value) });
};

// Render template for status select options
const statusOptionTemplate = (slotProps) => {
  return h(Tag, { value: slotProps.option.value, severity: getSeverity(slotProps.option.value) });
};

const onRowEditInit = (event) => {
  const data = { ...event.data };
  rowForm.initRow(data);
};

const onRowEditCancel = () => {
  rowForm.cancel();
};

const onRowEditSave = async (event) => {
  // Stop PrimeVue's default close behavior synchronously
  stopPrimeVueEvent(event);

  // Validate the form
  const isValid = await rowForm.validate();

  if (!isValid) {
    showToast({ group: 'top-toast', severity: 'warn', summary: 'Validation Error', detail: 'Please correct the errors before saving.' });
    // Keep the editor open for the current row
    await reopenEditorForId(getCurrentRowId());
    return;
  }

  // Commit the form back to the active row
  rowForm.commit();

  const normalized = rowForm.normalize((data) => data);

  // Perform the update and refresh networks
  try {
    const networkModel = new NetworkModel(normalized);
    const result = await updateNetwork(networkModel);

    // Accept either a thrown error or a non-throwing failure result.
    const success = (result && (result.ok === true || result.success === true)) || result == null || typeof result === 'undefined' || !!result;
    if (!success) {
      // Non-throwing failure response from service - present error and reopen editor
      showToast({ group: 'top-toast', severity: 'error', summary: 'Update Failed', detail: result?.message || 'Failed to update network.' });
      await reopenEditorForId(getCurrentRowId());
      return;
    }

    await appStore.fetchAllNetworks();
    // close the row editor since save succeeded
    editingRows.value = [];
    showToast({ group: 'top-toast', severity: 'success', summary: 'Network Updated', detail: `${normalized.networkName} (${normalized.networkId}) updated successfully.` });
  } catch (err) {
    showToast({ group: 'top-toast', severity: 'error', summary: 'Update Failed', detail: err?.message || 'Failed to update network.' });
    // Reopen editor so user can try again
    await reopenEditorForId(getCurrentRowId());
  }
};
</script>

<style scoped>
.header-components {
  display: flex;
  justify-content: flex-end;
}
</style>